// v10/src/components/admin/lifespan/ComponentCard.js
//
// Card visual de un componente con vida límite (A6.1).
// Muestra nombre, categoría, estado, barra de progreso por horas (si aplica),
// fechas, y acciones (placeholders en Tarea 1).
//
// El color del badge y de la barra viene del backend en `status_color`,
// no se hardcodea — para que cualquier ajuste futuro de umbrales en el service
// se refleje sin tocar el frontend.

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cog, Fan, Zap, Battery, Flame, Shield, Wind, Sparkle, Droplet, Wrench,
  MoreVertical, Edit3, Trash2, FileUp, History, RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Iconos por categoría — definidos en el brief.
const CATEGORY_ICONS = {
  engine:            Cog,
  propeller:         Fan,
  magneto:           Zap,
  elt_battery:       Battery,
  fire_extinguisher: Flame,
  seatbelt:          Shield,
  pitot_tube:        Wind,
  spark_plug:        Sparkle,
  oil_filter:        Droplet,
  other:             Wrench,
};

const STATUS_LABEL_KEYS = {
  operational: 'component_status_operational',
  next:        'component_status_next',
  critical:    'component_status_critical',
  expired:     'component_status_expired',
};

// Fallback de color si el backend no envía status_color.
const STATUS_COLOR_FALLBACK = {
  operational: '#16a34a',
  next:        '#eab308',
  critical:    '#ea580c',
  expired:     '#dc2626',
};

// Convierte "RRGGBB" a rgba con alpha para el fondo del badge/barra.
const hexToRgba = (hex, alpha = 0.12) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return `rgba(99,102,241,${alpha})`;
  }
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return `rgba(99,102,241,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

// Formato de fecha DD/MM/AAAA a partir de un string ISO o YYYY-MM-DD.
const formatDateAR = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// Formato es-AR: separador miles con punto, decimales con coma.
const formatHours = (n, locale = 'es-AR') => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 });
};

const ComponentCard = ({ component, darkMode, canManage, canDelete = canManage, locale = 'es-AR', onAction }) => {
  const { t } = useTranslation('admin');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const status = component.status || 'operational';
  const statusColor = component.status_color || STATUS_COLOR_FALLBACK[status] || '#64748b';
  const statusLabel = t(STATUS_LABEL_KEYS[status] || 'component_status_operational');
  const categoryLabel = component.category
    ? t(`component_category_${component.category}`, { defaultValue: component.category })
    : '';

  const Icon = CATEGORY_ICONS[component.category] || Wrench;

  // Cálculos de horas (numeric strings → number)
  const tboHours = component.tbo_hours != null ? Number(component.tbo_hours) : null;
  const hoursUsed = component.hours_used != null ? Number(component.hours_used) : null;
  const hoursRemaining = component.hours_remaining != null ? Number(component.hours_remaining) : null;
  const hoursProgressRaw = component.hours_progress_percent != null
    ? Number(component.hours_progress_percent) : null;
  const hasHoursTbo = Number.isFinite(tboHours) && tboHours > 0;
  const progressPercent = hasHoursTbo && Number.isFinite(hoursProgressRaw)
    ? Math.max(0, Math.min(100, hoursProgressRaw))
    : 0;
  const isHoursOverdue = hasHoursTbo && Number.isFinite(hoursRemaining) && hoursRemaining <= 0;

  // Cálculos de fecha
  const hasCalendarTbo = component.tbo_calendar_months != null;
  const expirationDate = component.expiration_date || null;
  const daysRemaining = component.days_remaining != null ? Number(component.days_remaining) : null;
  const isDateOverdue = hasCalendarTbo && Number.isFinite(daysRemaining) && daysRemaining <= 0;

  const cardBg     = darkMode ? 'rgba(255,255,255,0.03)' : 'white';
  const cardBorder = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const fgPrimary  = darkMode ? 'white' : '#0f172a';
  const fgMuted    = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgFaint    = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';
  const trackBg    = darkMode ? 'rgba(255,255,255,0.07)' : '#f1f5f9';

  const badgeBg = hexToRgba(statusColor, 0.14);
  const iconBg  = hexToRgba(statusColor, 0.14);

  const handleAction = (action) => {
    setMenuOpen(false);
    if (onAction) onAction(action, component);
  };

  // Subtexto debajo del nombre: "Manufacturer · S/N XXX" o solo lo que esté.
  const subtitleParts = [];
  if (component.manufacturer) subtitleParts.push(component.manufacturer);
  if (component.serial_number) {
    subtitleParts.push(`${t('component_card_serial_number')}: ${component.serial_number}`);
  }
  const subtitle = subtitleParts.join(' · ');

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      style={{
        background: cardBg,
        border: cardBorder,
        borderRadius: 20,
        padding: 20,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        position: 'relative',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 44, height: 44, borderRadius: 14,
            background: iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon style={{ width: 22, height: 22, color: statusColor }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary,
              letterSpacing: '-0.2px', lineHeight: 1.25,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
            title={component.name}
          >
            {component.name}
          </h3>
          {(categoryLabel || subtitle) && (
            <p
              style={{
                margin: '4px 0 0', fontSize: 12, color: fgMuted, lineHeight: 1.35,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
              title={subtitle ? `${categoryLabel} · ${subtitle}` : categoryLabel}
            >
              {categoryLabel}
              {categoryLabel && subtitle ? ' · ' : ''}
              {subtitle}
            </p>
          )}
        </div>

        {/* Badge de estado */}
        <span
          style={{
            fontSize: 10.5, fontWeight: 800,
            padding: '4px 10px', borderRadius: 999,
            background: badgeBg, color: statusColor,
            flexShrink: 0,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
          }}
        >
          {statusLabel}
        </span>

        {/* Kebab — solo si puede gestionar */}
        {canManage && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setMenuOpen((p) => !p)}
              aria-label={t('component_card_action_menu')}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: 6, borderRadius: 10, display: 'flex',
                color: fgMuted,
              }}
            >
              <MoreVertical style={{ width: 18, height: 18 }} />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                  style={{
                    position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                    minWidth: 220,
                    background: darkMode ? '#1f2937' : 'white',
                    border: cardBorder,
                    borderRadius: 14, padding: 6,
                    boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                    zIndex: 30,
                  }}
                >
                  <MenuItem
                    icon={FileUp}
                    label={t('component_card_action_upload_337')}
                    onClick={() => handleAction('upload_337')}
                    darkMode={darkMode}
                  />
                  <MenuItem
                    icon={History}
                    label={t('component_card_action_view_history')}
                    onClick={() => handleAction('view_history')}
                    darkMode={darkMode}
                  />
                  {canDelete && (
                    <MenuItem
                      icon={Trash2}
                      label={t('component_card_action_delete')}
                      onClick={() => handleAction('delete')}
                      darkMode={darkMode}
                      danger
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Bloque de horas (si tiene tbo_hours) */}
      {hasHoursTbo && (
        <div>
          <div
            style={{
              width: '100%', height: 10, borderRadius: 999,
              background: trackBg, overflow: 'hidden', marginBottom: 8,
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={{
                height: '100%',
                background: statusColor,
                borderRadius: 999,
              }}
            />
          </div>
          <div
            style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              gap: 12, fontSize: 12.5, color: fgMuted,
            }}
          >
            <span>
              {isHoursOverdue
                ? t('component_card_horometer_overdue', {
                    used: formatHours(hoursUsed, locale),
                    tbo: formatHours(tboHours, locale),
                    exceeded: formatHours(Math.abs(hoursRemaining), locale),
                  })
                : t('component_card_horometer_progress', {
                    used: formatHours(hoursUsed, locale),
                    tbo: formatHours(tboHours, locale),
                    remaining: formatHours(hoursRemaining, locale),
                  })}
            </span>
            <strong style={{ color: fgPrimary, fontWeight: 800 }}>
              {Math.round(progressPercent)}%
            </strong>
          </div>
        </div>
      )}

      {/* Bloque de fecha (si tiene tbo_calendar_months) */}
      {hasCalendarTbo && expirationDate && (
        <p style={{ margin: 0, fontSize: 12.5, color: fgMuted }}>
          {isDateOverdue
            ? t('component_card_expired_on', {
                date: formatDateAR(expirationDate),
                count: Math.abs(daysRemaining || 0),
              })
            : t('component_card_expires_on_with_days', {
                date: formatDateAR(expirationDate),
                count: daysRemaining || 0,
              })}
        </p>
      )}

      {/* Fecha de instalación */}
      {component.installed_at_date && (
        <p style={{ margin: 0, fontSize: 11.5, color: fgFaint }}>
          {t('component_card_installed_on', { date: formatDateAR(component.installed_at_date) })}
        </p>
      )}

      {/* Acciones principales — solo si puede gestionar */}
      {canManage && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          <ActionButton
            icon={Edit3}
            label={t('component_card_action_edit')}
            onClick={() => handleAction('edit')}
            darkMode={darkMode}
            variant="secondary"
          />
          <ActionButton
            icon={RefreshCw}
            label={t('component_card_action_replace')}
            onClick={() => handleAction('replace')}
            darkMode={darkMode}
            variant="primary"
          />
        </div>
      )}
    </motion.div>
  );
};

// ── Subcomponentes ──────────────────────────────────────────────────

const MenuItem = ({ icon: Icon, label, onClick, darkMode, danger = false }) => {
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
        background: 'transparent', color: danger ? '#ef4444' : fgPrimary,
        fontFamily: 'inherit', fontSize: 13, fontWeight: 600, textAlign: 'left',
      }}
    >
      <Icon style={{ width: 15, height: 15 }} />
      {label}
    </button>
  );
};

const ActionButton = ({ icon: Icon, label, onClick, darkMode, variant = 'secondary' }) => {
  const variants = {
    primary: {
      background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(79,70,229,0.25)',
    },
    secondary: {
      background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
      color: darkMode ? 'rgba(255,255,255,0.85)' : '#475569',
    },
  };
  return (
    <motion.button
      type="button"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 14px', borderRadius: 11, border: 'none', cursor: 'pointer',
        fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
        ...variants[variant],
      }}
    >
      <Icon style={{ width: 14, height: 14 }} />
      {label}
    </motion.button>
  );
};

export default ComponentCard;
